import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { CalcUnsavedChangesModalComponent } from 'src/app/components/modal/calc-unsaved-changes-modal/calc-unsaved-changes-modal.component';
import { ModalService } from '../../modal-service/modal.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { StepStateOptions } from 'src/app/models/step-state-options';
import { HouseholdQuoteStateService } from '../../household-quote-state-service/household-quote-state.service';
import { QuoteSteps } from 'src/app/models/enums/quote-steps';
import { ConfirmModalActionsVm } from 'src/app/models/modal-actions/confirm-modal-action';
import { FormBuilderService } from '../../form-builder/form-builder.service';
import { AlertTypes } from 'src/app/models/enums/inline-alert';
import { CalcMaxContentModalComponent } from 'src/app/components/modal/calc-max-contents-modal/calc-max-content-modal.component';
import { GoogleTagManagerService } from '../../google-analytics/google-tag-manager.service';

@Injectable({
    providedIn: 'root'
})
export class CalculatorService {
    private canExitCalculator$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private exitCalcBtnVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private canShowMaxValueModal: boolean = true;

    constructor(
        public modalService: ModalService,
        public householdQuoteStateService: HouseholdQuoteStateService,
        public formBuilderService: FormBuilderService,
        public gtmService: GoogleTagManagerService
    ) { }

    public setCanExitCalculator(canExit: boolean): void {
        this.canExitCalculator$.next(canExit);
    }

    public getCanExitCalculator(): Observable<boolean> {
        return this.canExitCalculator$.asObservable();
    }

    public setExitCalcBtnVisible(visible: boolean): void {
        this.exitCalcBtnVisible$.next(visible);
    }

    public getExitCalcBtnVisible(): Observable<boolean> {
        return this.exitCalcBtnVisible$.asObservable();
    }

    public calculateTotalOnChange(formGroup: FormGroup, alarmControl: AbstractControl<string>) {
        const hasAlarm = alarmControl.value?.startsWith('Yes');

        formGroup.valueChanges.subscribe(data => {
            let totalValue = 0;

            data.rooms.forEach((room, i) => {
                let roomTotal: number = 0;
                for (var key in room?.roomElements) {
                    roomTotal += room?.roomElements[key];
                };

                let rooms = formGroup.get('rooms') as FormArray;
                rooms.controls[i].get('subtotal').setValue(roomTotal, { emitEvent: false });
                totalValue += roomTotal;
            });
            formGroup.get('totalValue').setValue(totalValue, { emitEvent: false });

            if (formGroup.get('totalValue').value > (hasAlarm ? 210000 : 150000)) {
                if (this.canShowMaxValueModal) {
                    this.openMaxContentsValuePopup();
                }
                this.canShowMaxValueModal = false;
            } else {
                this.canShowMaxValueModal = true;
            }
        })
    }

    public calculateTotal(formGroup: FormGroup) {
        let totalValue = 0;

        formGroup.value.rooms.forEach((room, i) => {
            let roomTotal: number = 0;
            for (var key in room?.roomElements) {
                roomTotal += room?.roomElements[key];
            };

            let rooms = formGroup.get('rooms') as FormArray;
            rooms.controls[i].get('subtotal').setValue(roomTotal);
            totalValue += roomTotal;
        });

        formGroup.get('totalValue').setValue(totalValue, { emitEvent: false });
    }

    public openUnsavedChangesPopup() {

        let closeAction = () => {
            this.setCanExitCalculator(true);
            this.exitCalculator();
            this.gtmService.trackAction('home_contents_calculator', 'completed', undefined,undefined,'home_contents_calculator_success',false)
            this.modalService.dismissAll();
        };

        let customAction = () => {
            this.setCanExitCalculator(false);
            this.modalService.dismissAll();
        };

        let resultOption: ConfirmModalActionsVm = {
            title: 'Exit without saving?',
            confirmText: 'Continue calculating',
            cancelText: 'Exit without saving',
            customAction: customAction,
            customCloseAction: closeAction,
            description: 'You have unsaved information, which you will lose if you exit now.',
            inputs: undefined,
            iconType: AlertTypes.warningAlert
        }

        this.modalService.openModalComponent(CalcUnsavedChangesModalComponent, resultOption, false, true);
        return false;
    }

    public exitCalculator(): void {
        let options = new StepStateOptions();
        options.backNavigate = true;
        this.householdQuoteStateService.setStepState(QuoteSteps.contentsCover, options);
    }

    public setContentsCoverValue(totalValue: number) {
        let formGroup = this.formBuilderService.getQuoteForm().controls.aboutCoverFormGroup;
        let contentsControl = formGroup.controls.contentsSI;
        let calculatorUsed = formGroup.controls.contentsCalcUsed;
        let calculatorUsedEdited = formGroup.controls.contentsCalcUsedEdited;
        contentsControl.setValue(totalValue);
        contentsControl.updateValueAndValidity();
        contentsControl.markAsDirty();
        contentsControl.markAsTouched();
        calculatorUsed.setValue(true);
        
        calculatorUsedEdited.setValue(false);
    }

    private openMaxContentsValuePopup() {
        let customAction = () => {
            this.modalService.dismissAll();
        };

        let resultOption: ConfirmModalActionsVm = {
            title: 'Maximum contents value reached',
            cancelText: 'Back to calculator',
            customAction: customAction,
            customCloseAction: undefined,
            description: `The maximum contents value we can insure for a property is 
            <ul><li> €150,000 (without an alarm)</li>
            <li> €210,000 (with an alarm) </li></ul></br>
            Go back to review your contents information to ensure it’s accurate and recalculate. `,
            inputs: undefined,
            iconType: AlertTypes.warningAlert
        }

        this.modalService.openModalComponent(CalcMaxContentModalComponent, resultOption, false, true);
        return false;
    }
}
